import { Flex, Stack } from "@eworkit/iris";
import { FormikHelpers } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { IEditableSectionProps } from "@/components/EditableSection";
import { Header } from "@/components/Header";
import { createCompanyContact, fetchCompanyContact, ICompanyContact, putCompanyContact } from "@/data/companies";
import { useCompanyContext } from "@/hooks/User";
import { useToasts } from "@/views/components/Toast/useToasts";
import { CompanyContactPreview } from "@/views/Settings/CompanySettings/CompanyContactDetails/CompanyContact/CompanyContactPreview";
import { SupplierContactEdit } from "@/views/Settings/CompanySettings/CompanyContactDetails/CompanyContact/CompanyContactSectionEdit";

interface ICompanyContactSectionProps {
  editableProps: IEditableSectionProps;
}

export const CompanyContactSection = ({ editableProps }: ICompanyContactSectionProps) => {
  const { t } = useTranslation("session");
  const Company = useCompanyContext();
  const { addToast } = useToasts();

  const { data: fetchedContact, refetch } = useQuery(
    `/api/companies/company-contact/${Company.id}`,
    () => fetchCompanyContact(Company.id),
    {
      enabled: !!Company.id,
    },
  );

  const contact = useMemo(
    () =>
      fetchedContact || {
        supplierContactPhone: { phoneCode: "", phoneNumber: "", phonePrefix: "" },
        supplierEmail: "",
        financeContactPhone: { phoneCode: "", phoneNumber: "", phonePrefix: "" },
        financeEmail: "",
      },
    [fetchedContact],
  );

  const prepareContractPayload = (values: ICompanyContact) => {
    const isSupplierContactPhoneEmpty =
      values.supplierContactPhone && Object.values(values.supplierContactPhone).every(value => !value);

    if (isSupplierContactPhoneEmpty) values.supplierContactPhone = null;

    return values;
  };

  const handleSubmit = async (values: ICompanyContact, actions: FormikHelpers<ICompanyContact>) => {
    const action = values.id ? "update" : "creation";

    try {
      const payload = prepareContractPayload(values);

      action === "update"
        ? await putCompanyContact(payload.id!, payload)
        : await createCompanyContact(Company.id, payload);

      actions.setSubmitting(false);
      editableProps.handleCancelClick();
      await refetch();

      addToast({
        text: t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.CONTACT.TOAST.SUCCESS", { action }),
        type: "success",
      });
    } catch (e) {
      addToast({
        text: t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.CONTACT.TOAST.FAILURE", { action }),
        type: "danger",
      });
    }
  };

  return (
    <Stack gap="xs">
      <Flex mb="md">
        <Header size="xs" text={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.CONTACT.HEADER")} />
      </Flex>

      {editableProps.editMode ? (
        <SupplierContactEdit
          source={Company}
          initialValues={contact}
          onSubmit={handleSubmit}
          editableProps={editableProps}
          onCancel={editableProps.handleCancelClick}
        />
      ) : (
        <CompanyContactPreview editableProps={editableProps} contacts={contact} />
      )}
    </Stack>
  );
};
